// Campaign Center «Προτάσεις» (handoff v08 #24): 1–3 explainable campaign proposals from the existing segments,
// consent, follow-up history, the calendar and market notes the advisor stored with a source. Each shows its
// consent-filtered audience, contact goal, schedule, editable draft (from the theme library) and KPIs. Approval is an
// explicit tick + button and creates the campaign through prepareCampaign (audience frozen, nothing sent, no task yet).
// Logic: packages/domain/src/campaign-recommendations.ts.
import { useMemo, useState } from 'react';
import {
  CAMPAIGN_THEMES, KPI_MIN_SAMPLE, NOT_USED_INPUTS, RECOMMENDATION_INPUTS, REC_CHANNELS, approveRecommendation, deleteMarketNote, dismissRecommendation, draftFor, evaluateTheme,
  marketNotes, recommendCampaigns, saveMarketNote, themeOf, themeText, type CampaignRecommendation, type Channel, type RecReason,
} from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Empty, Field, Seg } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';

type Ev = { target: HTMLInputElement };
type TaEv = { target: HTMLTextAreaElement };

export function SuggestionsTab() {
  const rt = useRuntime();
  const { t, view, today, locale } = rt;
  const recs = useMemo(() => recommendCampaigns(view, today, { locale }), [view, today, locale]);
  return (
    <div className="stack" data-testid="campaign-suggestions">
      <section className="card card-pad calm">
        <p style={{ margin: 0 }}>{t('recs.intro')}</p>
        <p className="tiny muted" style={{ marginBottom: 0 }}>{t('recs.never_sends')}</p>
      </section>
      {recs.length ? recs.map((r) => <RecCard key={r.key} rec={r} />) : <section className="card"><Empty icon="spark">{t('recs.none')}</Empty></section>}
      <InputsCard />
      <MarketNotes />
    </div>
  );
}

export function reasonText(rt: Runtime, r: RecReason): string {
  const p: Record<string, string | number> = { ...r.params };
  if (typeof p.segment === 'string') p.segment = rt.t(`segment.${p.segment}`);
  if (typeof p.date === 'string') p.date = rt.i18n.date(p.date);
  if (typeof p.month === 'number') p.month = new Intl.DateTimeFormat(rt.locale, { month: 'long' }).format(new Date(Date.UTC(2026, (p.month as number) - 1, 15)));
  return rt.t(`recs.reason.${r.key}`, p);
}

function RecCard({ rec }: { rec: CampaignRecommendation }) {
  const rt = useRuntime();
  const { t, i18n, view, today, locale } = rt;
  const th = themeOf(rec.themeKey)!;
  const text = themeText(th, locale);
  const [channel, setChannel] = useState<Channel>(rec.channel);
  const shown = useMemo(() => (channel === rec.channel ? rec : evaluateTheme(view, th, today, { channel, locale }).rec), [channel, rec, view, today, locale, th]);
  const [title, setTitle] = useState(text.title);
  const [msg, setMsg] = useState(rec.draft);
  const [ok, setOk] = useState(false);
  const pickChannel = (ch: Channel) => { setChannel(ch); setMsg(draftFor(th, ch, locale)); setOk(false); };
  const approve = async () => {
    if (!shown) return;
    const cs = await rt.run((v, c) => approveRecommendation(v, { themeKey: rec.themeKey, channel, locale, title, message: msg, approved: ok, shown: { audience: shown.audience, kpis: shown.kpis, schedule: shown.schedule, contactGoal: shown.contactGoal, reasons: shown.reasons } }, c), 'recs.approved');
    if (cs?.entityId) navigate(`/campaigns/${cs.entityId}`);
  };
  const kpi = (n: number | null, fmt: (x: number) => string = (x) => i18n.number(x)) => (n === null ? <span className="muted small">{t('recs.kpi_measured')}</span> : <b className="num">{fmt(n)}</b>);
  return (
    <article className="card card-pad" data-testid="rec-card" data-theme={rec.themeKey}>
      <div className="row spread" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <b style={{ fontSize: 16 }}>{text.title}</b>
          <div className="small muted">{text.purpose}</div>
          <div className="tiny muted">{t(`segment.${rec.segmentKey}`)} · {t(`segment.criteria.${rec.segmentKey}`)}</div>
        </div>
        {rec.season && <span className="chip gold">{t(`recs.season.${rec.season}`)}</span>}
      </div>
      <div className="upper" style={{ marginTop: 10 }}>{t('recs.why')}</div>
      <ul className="small" data-testid="rec-reasons">{rec.reasons.map((r, i) => <li key={i}>{reasonText(rt, r)}</li>)}</ul>
      <div className="grid grid-2" style={{ alignItems: 'start', gap: 12 }}>
        <div>
          <div className="upper">{t('recs.audience')}</div>
          <Field label={t('campaigns.channel_label')} group><Seg values={REC_CHANNELS} value={channel} onChange={pickChannel} label={(x) => `${t(`campaigns.channel.${x}`)} · ${rec.byChannel[x] ?? 0}`} testId="rec-channel" /></Field>
          {shown ? <div className="kv" data-testid="rec-audience">
            <div><span>{t('recs.eligible')}</span><b data-testid="rec-eligible">{shown.audience.eligible}</b></div>
            <div><span>{t('recs.in_segment')}</span>{shown.audience.members}</div>
            {Object.entries(shown.audience.excluded).map(([k, n]) => <div key={k}><span>{t(`audience.reason.${k}`)}</span>{n}</div>)}
            {shown.audience.recentlyContacted > 0 && <div><span>{t('recs.recent')}</span>{shown.audience.recentlyContacted}</div>}
            {shown.audience.incomplete > 0 && <div><span>{t('segments.incomplete_short')}</span>{shown.audience.incomplete}</div>}
          </div> : <p className="small muted" data-testid="rec-no-eligible">{t('recs.no_eligible_channel')}</p>}
          <p className="tiny muted">{t('recs.audience_note')}</p>
          {shown && <>
            <div className="upper">{t('recs.goal_title')}</div>
            <p className="small" data-testid="rec-goal">{t('recs.goal', { total: shown.contactGoal.total, perDay: shown.contactGoal.perDay, days: shown.contactGoal.days })}</p>
            <div className="upper">{t('recs.schedule')}</div>
            <ol className="small" data-testid="rec-schedule">{shown.schedule.steps.map((s, i) => <li key={i}>{i18n.date(s.day)} — {t(`campaign.step.${s.action}`)}</li>)}</ol>
            <p className="tiny muted">{t('recs.schedule_note', { start: i18n.date(shown.schedule.start), end: i18n.date(shown.schedule.end) })}</p>
            <div className="upper">{t('recs.kpis')}</div>
            <div className="kv" data-testid="rec-kpis">
              <div><span>{t('recs.kpi.contacts')}</span><b className="num">{shown.kpis.contacts}</b></div>
              <div><span>{t('recs.kpi.positive')}</span>{kpi(shown.kpis.positiveReplies)}</div>
              <div><span>{t('recs.kpi.meetings')}</span>{kpi(shown.kpis.meetings)}</div>
              <div><span>{t('recs.kpi.conversion')}</span>{kpi(shown.kpis.conversion, (x) => `${i18n.number(x)}%`)}</div>
            </div>
            <p className="tiny muted">{shown.kpis.basis === 'own_history' ? t('recs.kpi_basis_own', { sample: shown.kpis.sample }) : t('recs.kpi_basis_none', { sample: shown.kpis.sample, min: KPI_MIN_SAMPLE })}</p>
          </>}
          {rec.market.length > 0 && <>
            <div className="upper">{t('recs.market')}</div>
            <ul className="small">{rec.market.map((n) => <li key={n.id}><b>{n.title}</b> — {n.url ? <a href={n.url} target="_blank" rel="noreferrer">{n.source}</a> : n.source}, {i18n.date(n.date)}</li>)}</ul>
          </>}
        </div>
        <div>
          <div className="upper">{t('recs.draft')}</div>
          <Field label={t('recs.title_field')}><input className="input" value={title} onChange={(e: Ev) => setTitle(e.target.value)} data-testid="rec-title" /></Field>
          {channel === 'email' && <Field label={t('templates.subject')}><input className="input" value={msg.subject ?? ''} onChange={(e: Ev) => setMsg({ ...msg, subject: e.target.value })} /></Field>}
          <Field label={t('recs.message')}><textarea className="input" rows={7} value={msg.body} onChange={(e: TaEv) => setMsg({ ...msg, body: e.target.value })} data-testid="rec-body" /></Field>
          <p className="tiny muted">{t('campaigns.verify_claims')}</p>
        </div>
      </div>
      <label className="row" style={{ gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={ok} onChange={(e: Ev) => setOk(e.target.checked)} disabled={!shown} data-testid="rec-approve-check" />
        <span className="small">{t('recs.approve_check', { count: shown?.audience.eligible ?? 0 })}</span>
      </label>
      <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
        <button className="btn btn-primary" disabled={!ok || !shown} onClick={() => void approve()} data-testid="rec-approve"><Icon name="check" />{t('recs.approve')}</button>
        <button className="btn btn-ghost" onClick={() => void rt.run((v, c) => dismissRecommendation(v, rec.themeKey, c), 'recs.dismissed')} data-testid="rec-dismiss">{t('recs.dismiss')}</button>
      </div>
    </article>
  );
}

function InputsCard() {
  const { t } = useRuntime();
  return (
    <section className="card card-pad" data-testid="rec-inputs">
      <details><summary className="small"><b>{t('recs.inputs_title')}</b></summary>
        <div className="upper" style={{ marginTop: 8 }}>{t('recs.inputs_used')}</div>
        <ul className="small">{RECOMMENDATION_INPUTS.map((k) => <li key={k}>{t(`recs.input.${k}`)}</li>)}</ul>
        <div className="upper">{t('recs.inputs_not_used')}</div>
        <ul className="small">{NOT_USED_INPUTS.map((k) => <li key={k}>{t(`recs.not_used.${k}`)}</li>)}</ul>
        <p className="tiny muted">{t('recs.health_note')}</p>
      </details>
    </section>
  );
}

function MarketNotes() {
  const rt = useRuntime();
  const { t, i18n, view, today, locale } = rt;
  const notes = marketNotes(view);
  const [f, setF] = useState({ title: '', source: '', url: '', date: today, summary: '', themes: [] as string[] });
  const add = async () => { const cs = await rt.run((v, c) => saveMarketNote(v, { title: f.title, source: f.source, url: f.url, date: f.date, summary: f.summary, themes: f.themes }, c), 'recs.note_saved'); if (cs) setF({ title: '', source: '', url: '', date: today, summary: '', themes: [] }); };
  const toggle = (k: string) => setF({ ...f, themes: f.themes.includes(k) ? f.themes.filter((x) => x !== k) : [...f.themes, k] });
  return (
    <section className="card card-pad" data-testid="market-notes">
      <div className="section-title">{t('recs.market_title')}</div>
      <p className="small muted">{t('recs.market_hint')}</p>
      {notes.length > 0 && <ul className="small">{notes.map((n) => <li key={n.id} data-testid="market-note"><b>{n.title}</b> — {n.url ? <a href={n.url} target="_blank" rel="noreferrer">{n.source}</a> : n.source}, {i18n.date(n.date)} · {n.themes.map((k) => themeText(themeOf(k)!, locale).title).join(', ')} <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => deleteMarketNote(v, n.id, c), 'common.saved')}>{t('common.delete')}</button></li>)}</ul>}
      <details><summary className="small">{t('recs.note_add')}</summary>
        <div className="form-grid" style={{ marginTop: 8 }}>
          <Field label={t('recs.note_title')}><input className="input" value={f.title} onChange={(e: Ev) => setF({ ...f, title: e.target.value })} data-testid="note-title" /></Field>
          <Field label={t('recs.note_source')}><input className="input" value={f.source} onChange={(e: Ev) => setF({ ...f, source: e.target.value })} data-testid="note-source" /></Field>
          <Field label={t('recs.note_url')}><input className="input" value={f.url} onChange={(e: Ev) => setF({ ...f, url: e.target.value })} placeholder="https://" /></Field>
          <Field label={t('recs.note_date')} group><DateField value={f.date} max={today} onChange={(d) => setF({ ...f, date: d })} label={t('recs.note_date')} /></Field>
          <Field label={t('recs.note_summary')} span2><input className="input" value={f.summary} onChange={(e: Ev) => setF({ ...f, summary: e.target.value })} /></Field>
          <Field label={t('recs.note_themes')} span2 group><div className="row wrap" style={{ gap: 6 }}>{CAMPAIGN_THEMES.map((th) => <label key={th.key} className="chip" style={{ cursor: 'pointer' }}><input type="checkbox" checked={f.themes.includes(th.key)} onChange={() => toggle(th.key)} /> {themeText(th, locale).title}</label>)}</div></Field>
        </div>
        <button className="btn" onClick={() => void add()} data-testid="note-save">{t('recs.note_save')}</button>
      </details>
    </section>
  );
}
